import type { ParsedReservationError } from './generationRunContext';

export type ManuscriptTier =
  | 'short_manuscript'
  | 'standard_manuscript'
  | 'large_manuscript'
  | 'epic_manuscript';

export const MANUSCRIPT_CREDIT_REQUIRED_DIAGNOSTIC_TAG = '[MANUSCRIPT CREDIT REQUIRED]';
export const MANUSCRIPT_RESERVATION_CONTRACT_FAILURE_TAG = '[MANUSCRIPT RESERVATION CONTRACT FAILURE]';

export const TECHNICAL_RESERVATION_MESSAGE =
  'Chronovela could not verify your manuscript credit. No credit was used. Please try again.';

export interface ManuscriptReservationContractFailureDiagnostic {
  operation: string;
  normalizedOperation: string | null;
  acceptedOperations: string[];
  projectId: string;
  chapterNumber: number;
  paywallOpened: false;
}

export function logManuscriptReservationContractFailure(
  diagnostic: ManuscriptReservationContractFailureDiagnostic,
): void {
  console.warn(`${MANUSCRIPT_RESERVATION_CONTRACT_FAILURE_TAG} ${JSON.stringify(diagnostic)}`);
}

export interface ManuscriptCreditRequiredDiagnostic {
  projectId: string;
  requestedProduct: string;
  requiredCreditType: ManuscriptTier;
  availableBalance: number;
  reservationCreated: false;
  upgradeModalOpened: boolean;
  internalCode: string;
  chapterNumber: number;
}

export interface PaywallModalState {
  isOpen: boolean;
  reason: 'generation' | 'autowrite' | 'export' | 'sequel' | 'rewrite';
  highlightedTier?: ManuscriptTier;
  headline?: string;
  subheadline?: string;
}

export interface ReservationRejectionContext {
  error: ParsedReservationError;
  projectId: string;
  chapterNumber: number;
  targetChapterCount: number;
  manuscriptCreditBalances?: Partial<Record<ManuscriptTier, number>>;
}

export interface ReservationRejectionOutcome {
  openPaywall: boolean;
  paywallState?: PaywallModalState;
  creatorFeedMessage: string;
  technicalFailure: boolean;
  notifyReservationBlocked: boolean;
  diagnostic?: ManuscriptCreditRequiredDiagnostic;
  unwindGenerationUi: boolean;
  skipFinishGenerationRun: boolean;
}

const MANUSCRIPT_TIER_LABELS: Record<ManuscriptTier, string> = {
  short_manuscript: 'Short Manuscript Credit',
  standard_manuscript: 'Standard Manuscript Credit',
  large_manuscript: 'Large Manuscript Credit',
  epic_manuscript: 'Epic Manuscript Credit',
};

export function resolveRecommendedManuscriptTier(targetChapterCount: number): ManuscriptTier {
  const count = Math.max(1, targetChapterCount || 1);
  if (count <= 10) return 'short_manuscript';
  if (count <= 20) return 'standard_manuscript';
  if (count <= 30) return 'large_manuscript';
  return 'epic_manuscript';
}

export function getManuscriptCreditLabel(tier: ManuscriptTier): string {
  return MANUSCRIPT_TIER_LABELS[tier];
}

export function isInsufficientCreditReservationError(error: ParsedReservationError): boolean {
  if (error.kind === 'no_entitlement' || error.kind === 'insufficient_credits') {
    return true;
  }
  const reason = String(error.reason || '').toLowerCase();
  return (
    reason === 'no_manuscript_entitlement' ||
    reason === 'insufficient_rewrite_credits' ||
    reason === 'free_preview_already_used' ||
    reason === 'chapter_limit_exceeded'
  );
}

export function isTechnicalReservationFailure(error: ParsedReservationError): boolean {
  if (error.kind === 'rate_limited' || error.kind === 'invalid_state') {
    return true;
  }
  if (error.kind === 'missing_project') {
    return true;
  }
  const reason = String(error.reason || '').toLowerCase();
  if (
    reason === 'network_error' ||
    reason === 'gateway_error' ||
    reason === 'server_error' ||
    reason === 'reservation_expired' ||
    reason === 'invalid_operation' ||
    reason === 'missing_fields'
  ) {
    return true;
  }
  const status = error.httpStatus;
  if (typeof status === 'number' && (status === 502 || status === 503 || status === 504 || status >= 500)) {
    return true;
  }
  if (error.kind === 'unknown' && !isInsufficientCreditReservationError(error)) {
    return true;
  }
  return false;
}

export function shouldOpenPaywallForReservationError(error: ParsedReservationError): boolean {
  return isInsufficientCreditReservationError(error) && !isTechnicalReservationFailure(error);
}

export function buildManuscriptCreditRequiredFeedMessage(chapterNumber: number): string {
  return `A manuscript credit is required before Chapter ${chapterNumber} can be generated. Opening upgrade options.`;
}

export function buildManuscriptCreditPaywallCopy(tier: ManuscriptTier): {
  headline: string;
  subheadline: string;
} {
  const label = getManuscriptCreditLabel(tier);
  return {
    headline: `${label} is required to generate this manuscript.`,
    subheadline: 'Choose a manuscript package to continue.',
  };
}

export function logManuscriptCreditRequiredDiagnostic(
  diagnostic: ManuscriptCreditRequiredDiagnostic,
): void {
  console.info(
    `${MANUSCRIPT_CREDIT_REQUIRED_DIAGNOSTIC_TAG}\n${JSON.stringify(diagnostic)}`,
  );
}

export function resolveReservationRejectionOutcome(
  context: ReservationRejectionContext,
): ReservationRejectionOutcome {
  const { error, projectId, chapterNumber, targetChapterCount, manuscriptCreditBalances } = context;
  const highlightedTier = resolveRecommendedManuscriptTier(targetChapterCount);
  const availableBalance = manuscriptCreditBalances?.[highlightedTier] ?? 0;
  const internalCode = String(error.reason || error.kind).toUpperCase();

  if (shouldOpenPaywallForReservationError(error)) {
    const copy = buildManuscriptCreditPaywallCopy(highlightedTier);
    const diagnostic: ManuscriptCreditRequiredDiagnostic = {
      projectId,
      requestedProduct: getManuscriptCreditLabel(highlightedTier),
      requiredCreditType: highlightedTier,
      availableBalance,
      reservationCreated: false,
      upgradeModalOpened: true,
      internalCode,
      chapterNumber,
    };

    return {
      openPaywall: true,
      paywallState: {
        isOpen: true,
        reason: 'generation',
        highlightedTier,
        headline: copy.headline,
        subheadline: copy.subheadline,
      },
      creatorFeedMessage: buildManuscriptCreditRequiredFeedMessage(chapterNumber),
      technicalFailure: false,
      notifyReservationBlocked: false,
      diagnostic,
      unwindGenerationUi: true,
      skipFinishGenerationRun: true,
    };
  }

  return {
    openPaywall: false,
    creatorFeedMessage: TECHNICAL_RESERVATION_MESSAGE,
    technicalFailure: true,
    notifyReservationBlocked: true,
    unwindGenerationUi: true,
    skipFinishGenerationRun: true,
  };
}
